/**
 * Bounded-concurrency map — the WebClip's answer to the semaphore in Android's
 * `CheckRunner.checkRound`.
 *
 * `Promise.all` over 200 links would put 200 requests on the radio at once. They
 * do not go out in parallel so much as queue behind each other, and the ones
 * that wait longest are the ones that hit their timeout — which records a link
 * as unhealthy for a reason that has nothing to do with the link.
 */
export async function pool<T, R>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
  /** Called as each result lands, so the UI can move while the round runs. */
  onResult?: (result: R, index: number) => void,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  if (items.length === 0) return results;

  const width = Math.max(1, Math.min(limit, items.length));
  let cursor = 0;

  async function runner(): Promise<void> {
    for (;;) {
      const index = cursor++;
      if (index >= items.length) return;
      // `items[index]` is defined by the bound above; the cast is only here to
      // satisfy `noUncheckedIndexedAccess`.
      const result = await worker(items[index] as T, index);
      results[index] = result;
      onResult?.(result, index);
    }
  }

  // No `Promise.all` rejection path on purpose: `worker` is `probeReachable`,
  // which is total. If a future caller passes something that can throw, one
  // failure would abandon the other lanes' results — so this stays a contract:
  // **the worker must not throw.**
  await Promise.all(Array.from({ length: width }, runner));
  return results;
}
